import Framework from '@greenville/framework';
import { getSnapshot } from 'mobx-state-tree';
import { dateTimeUtils, coverageDuplicationUtils, validationUtils } from '@aet/components';
import PlaylistUtils from '../../../common/utils/PlaylistUtils';

import {
  ASSIGNMENT_TYPES,
  ASSIGNMENT_STATUS_TYPES,
  TIME_FORMATS,
  LAST_ASSIGNMENT_DEFAULTS_KEY,
  ASSIGNMENT_TITLE_CONFIG,
  DEFAULT_MAX_ASSIGNMENT_TITLE_LENGTH,
  DEFAULT_SUPPORTED_CHAR
} from '../common/Constants';

import CommonUtils from '../../../common/utils/CommonUtils';
import { computeDateAvailable } from '../../authhome/plugin/instructorCourseDashboard/assignmentSettingsHelpers';
import { hasAssignmentDueDatePassed } from './StudentDueDatesAdjustmentUtils';
import {
  YEAR_MONTH_DAY_FORMAT,
  DEFAULT_SYLLABUS_SCHEDULE_EXCLUDED_TYPES,
  SYLLABUS_SCHEDULE_EXCLUDED_TYPES,
  ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS
} from '../../../common/constants';

let containerContext = null;
// sets the context from aetassignmentsplugin
export const setContainerContext = (context) => { containerContext = context; };
export const getContainerContextValue = (key, fallback) => containerContext?.getContainerData?.('heroView', key) || fallback;

// Get syllabus schedule excluded types from AWS container data
export const getSyllabusScheduleExcludedTypes = () => getContainerContextValue(SYLLABUS_SCHEDULE_EXCLUDED_TYPES, DEFAULT_SYLLABUS_SCHEDULE_EXCLUDED_TYPES);

/**
 * Recursively extracts all items with type "chapter" from the asset response
 * and returns them as a flat array
 *
 * @param {Object} asset - The asset object containing children array
 * @returns {Array} - Flat array of chapter objects
 */
export default function getFlatChaptersList(asset) {
  const chapters = [];

  const extractChapters = (items) => {
    if (!items || !Array.isArray(items)) {
      return;
    }

    items.forEach((item) => {
      // If the item is of type "chapter", add it to our list
      if (item.type === 'chapter') {
        chapters.push(item);
      }

      // Recursively search through children
      if (item.children && Array.isArray(item.children)) {
        extractChapters(item.children);
      }
    });
  };

  // Start the extraction from the asset's children
  if (asset && asset.children) {
    extractChapters(asset.children);
  }

  return chapters;
}

/**
 * Helper function to build chapter URI/ID mappings
 * @param {Array} items - Asset children items
 * @param {Map} chapterUriToIdMap - Map to store URI to ID mappings
 * @param {Map} chapterIdToIdMap - Map to store ID to ID mappings
 */
export function buildChapterMappings(items, chapterUriToIdMap, chapterIdToIdMap) {
  if (!items || !Array.isArray(items)) return;

  items.forEach((item) => {
    if (item.type === 'chapter') {
      // Map both URI and ID to chapter ID
      chapterUriToIdMap.set(item.uri, item.id);
      chapterIdToIdMap.set(item.id, item.id); // Self-mapping for ID
    }

    if (item.children && item.children.length > 0) {
      buildChapterMappings(item.children, chapterUriToIdMap, chapterIdToIdMap);
    }
  });
}

/**
 * Helper function to map slates to chapters
 * @param {Array} slates - Array of slate objects
 * @param {Map} chapterUriToIdMap - Map of URI to chapter ID
 * @param {Map} chapterIdToIdMap - Map of ID to chapter ID
 * @param {Map} chapterToPageIdMap - Map to store chapter to page mappings
 */
export function mapSlatesToChapters(slates, chapterUriToIdMap, chapterIdToIdMap, chapterToPageIdMap) {
  slates.forEach((slate) => {
    if (slate.type === 'slate') {
      let chapterId = null;

      // Try matching by chapterUri first
      if (slate.chapterUri) {
        chapterId = chapterUriToIdMap.get(slate.chapterUri);
      }

      // If no match by URI, try direct ID matching (if slate has chapterId)
      if (!chapterId && slate.chapterId) {
        chapterId = chapterIdToIdMap.get(slate.chapterId);
      }

      // If we found a chapter match, add the slate ID
      if (chapterId) {
        if (!chapterToPageIdMap.has(chapterId)) {
          chapterToPageIdMap.set(chapterId, []);
        }
        chapterToPageIdMap.get(chapterId).push(slate.id);
      }
    }
  });
}

// Find any unmapped pages and traverse up to find parent chapter
export function findParentChapterForPage(requiredChapterId, assetData) {
  if (!requiredChapterId || !assetData || !assetData.children || !assetData.children.length > 0) return null;
  const findPageAndTraverseUp = (items, currentParentChapterId = null) => {
    let parentChapterId = null;
    items.find((item) => {
      if (!item || !item.id) return false;
      // If this is a chapter, update the parent chapter ID
      const nextParentChapterId = item.type === 'chapter' ? item.id : currentParentChapterId;
      // If we found the target pageId (slate/module), return the current parent chapter
      if (item.id === requiredChapterId) {
        parentChapterId = item.type === 'chapter' ? item.id : currentParentChapterId;
        return true;
      }
      // If this item has children, search recursively
      if (item.children && item.children.length > 0) {
        const result = findPageAndTraverseUp(item.children, nextParentChapterId);
        if (result !== null) {
          parentChapterId = result;
          return true;
        }
      }
      return false;
    });
    return parentChapterId;
  };
  return findPageAndTraverseUp(assetData.children);
}

/**
 * True when tocId exists in the asset TOC and (if expectedParentChapterId) is that chapter or its descendant.
 */
export function isTocIdValidForChapter(tocId, tocAsset, expectedParentChapterId = null) {
  if (!tocId || !tocAsset) return false;
  if (!findNodeTitleById(tocId, tocAsset)) return false;

  if (!expectedParentChapterId) {
    return !!findParentChapterForPage(tocId, tocAsset);
  }

  if (tocId === expectedParentChapterId) return true;

  return findParentChapterForPage(tocId, tocAsset) === expectedParentChapterId;
}

/**
 * Helper function to build chapter to page mapping
 * @param {Object} asset - Asset object with slates and children
 * @returns {Map} - Map of chapter ID to array of page IDs
 */
export function buildChapterToPageMapping(asset) {
  const chapterToPageIdMap = new Map();

  if (asset && asset.children) {
    // Build dual mapping: URI → chapter ID AND ID → chapter ID
    const chapterUriToIdMap = new Map();
    const chapterIdToIdMap = new Map(); // For direct ID matching

    buildChapterMappings(asset.children, chapterUriToIdMap, chapterIdToIdMap);

    // Handle case where slates exist (original behavior)
    if (asset.slates && Array.isArray(asset.slates)) {
      mapSlatesToChapters(asset.slates, chapterUriToIdMap, chapterIdToIdMap, chapterToPageIdMap);
    } else {
      // Handle case where slates is absent - map chapters to their modules (pages)
      // This creates a mapping where each chapter ID maps to an array of its module IDs
      const mapChaptersToModules = (items, parentChapterId = null) => {
        if (!items || !Array.isArray(items)) return;

        items.forEach((item) => {
          if (item.type === 'chapter') {
            // This is a chapter - map its children modules to it
            if (item.children && item.children.length > 0) {
              mapChaptersToModules(item.children, item.id);
            }
          } else if (item.type === 'module' && parentChapterId) {
            // This is a module under a chapter - add it to the chapter's page list
            if (!chapterToPageIdMap.has(parentChapterId)) {
              chapterToPageIdMap.set(parentChapterId, []);
            }
            chapterToPageIdMap.get(parentChapterId).push(item.id);
          }

          // Continue recursively for nested structures
          if (item.children && item.children.length > 0) {
            mapChaptersToModules(item.children, parentChapterId);
          }
        });
      };
      mapChaptersToModules(asset.children);

      const findUnmappedPages = (items) => {
        if (!items || !Array.isArray(items)) return;
        items.forEach((item) => {
          if (!item || !item.id) return;
          // Check if this is a page that wasn't mapped
          if (item.type === 'module' || item.type === 'slate') {
            const isMapped = Array.from(chapterToPageIdMap.values())
              .some(pageIds => pageIds.includes(item.id));
            // If not mapped, find parent chapter by traversing up
            if (!isMapped) {
              const chapterId = findParentChapterForPage(item.id, asset);
              if (chapterId) {
                if (!chapterToPageIdMap.has(chapterId)) {
                  chapterToPageIdMap.set(chapterId, []);
                }
                chapterToPageIdMap.get(chapterId).push(item.id);
              }
            }
          }
          if (item.children && item.children.length > 0) {
            findUnmappedPages(item.children);
          }
        });
      };

      findUnmappedPages(asset.children);
    }
  }
  return chapterToPageIdMap;
}

/**
 * Helper function to filter MCQs for a chapter
 * @param {Array} mcqQuestions - Array of MCQ questions
 * @param {Array} pageIds - Array of page IDs to match
 * @returns {Array} - Filtered MCQs that match the page IDs
 */
export function filterMCQsForChapter(mcqQuestions, pageIds) {
  return mcqQuestions.filter(mcq => mcq.pageId && pageIds.includes(mcq.pageId));
}

/**
 * Helper function to create assignment object
 * @param {Object} scheduleItem - Schedule item from syllabus
 * @param {Array} allMCQsForAssignment - Array of MCQs for this assignment
 * @returns {Object} - Assignment object
 */
export function createAssignmentObject(scheduleItem, allMCQsForAssignment) {
  return {
    assignmentId: null,
    assignmentName: scheduleItem.title,
    startDate: scheduleItem.date,
    dueDate: scheduleItem.date,
    weekNumber: scheduleItem.weekNumber,
    assignmentType: scheduleItem.type || 'homework',
    title: scheduleItem.title,
    textbookCoverage: scheduleItem.textbookCoverage ? {
      pages: scheduleItem.textbookCoverage.pages ? [...scheduleItem.textbookCoverage.pages] : [],
      sectionIds: scheduleItem.textbookCoverage.sectionIds?.length
        ? [...scheduleItem.textbookCoverage.sectionIds]
        : [],
      chapters: scheduleItem.textbookCoverage.chapters
        ? scheduleItem.textbookCoverage.chapters.map(chapter => ({
          chapterId: chapter.chapterId,
          chapterTitle: chapter.chapterTitle,
          num: chapter.num,
          title: chapter.title,
          selectedNodeId: chapter.selectedNodeId,
          selectedNodeType: chapter.selectedNodeType
        })) : []
    } : null,
    questions: JSON.parse(JSON.stringify(allMCQsForAssignment)),
    time: scheduleItem.time,
    timeZone: scheduleItem.timeZone,
    referenceId: scheduleItem.referenceId,
    isTitleGenerated: scheduleItem.isTitleGenerated,
    validationErrors: scheduleItem.validationErrors ? [...scheduleItem.validationErrors] : [],
    isValid: scheduleItem.isValid ?? !(scheduleItem.validationErrors?.length),
    dateNormalizationAction: scheduleItem.dateNormalizationAction ?? null,
    coverageDedupeAction: scheduleItem.coverageDedupeAction ?? null
  };
}

/**
 * Collect syllabus section entries, preserving chapter context when nested sections exist.
 */
function collectCoverageSectionEntries(coverage) {
  return (coverage?.chapters || []).flatMap((chapter) => (
    (chapter.sections || []).map((section) => ({
      chapterNum: chapter.num,
      sectionNum: typeof section === 'object' ? section?.num : section
    }))
  )).filter(({ sectionNum }) => sectionNum != null && String(sectionNum).trim() !== '');
}

/**
 * Resolve a section tocId from the flat mapping response, using analyse chapter context
 * when num repeats (Introduction/Summary).
 */
function findMappedSectionTocId(sectionNum, chapterNum, mappedChapters, mappedSections, tocAsset) {
  const num = String(sectionNum);
  const mappedChapter = chapterNum != null
    ? mappedChapters.find((chapter) => String(chapter.num) === String(chapterNum))
    : null;

  const candidates = mappedSections.filter(
    (section) => String(section.num) === num && section.tocId
  );
  if (!candidates.length) return undefined;

  const parentChapterId = mappedChapter?.tocId;
  if (parentChapterId && tocAsset) {
    const match = candidates.find(
      (section) => findParentChapterForPage(section.tocId, tocAsset) === parentChapterId
    );
    if (match) return match.tocId;
  }

  return candidates[0].tocId;
}

/**
 * Helper function to apply TOC mapping to syllabus data
 * Updates chapters with chapterId and sectionIds from TOC mapping response
 * @param {Object} syllabusData - Syllabus data object
 * @param {Array} mappedChapters - Array of mapped chapters from TOC service
 * @param {Array} mappedSections - Array of mapped sections from TOC service
 * @param {Array} chaptersList - Array of chapters from asset
 * @param {Object} [tocAsset] - Full asset TOC (parent chapter lookup when Prism chapter tocId is blank)
 * @returns {Object} - Updated syllabus data with chapterId mappings
 */
export function applyTocMappingToSyllabus(
  syllabusData,
  mappedChapters,
  mappedSections = [],
  chaptersList,
  tocAsset = null
) {
  // Deep clone to avoid mutating frozen objects
  const updatedData = JSON.parse(JSON.stringify(syllabusData));

  if (updatedData.syllabusInfo?.data?.schedule) {
    updatedData.syllabusInfo.data.schedule = updatedData.syllabusInfo.data.schedule.map((scheduleItem) => {
      const coverage = scheduleItem.textbookCoverage;
      const coverageSectionEntries = collectCoverageSectionEntries(coverage);

      if (coverage?.chapters || coverageSectionEntries.length > 0) {
        const mapChapterEntry = (chapter) => {
          if (chapter.chapterId) {
            if (isTocIdValidForChapter(chapter.chapterId, tocAsset)) {
              return chapter;
            }
            const { chapterId, id, ...rest } = chapter;

            return rest;
          }

          const mappedChapter = mappedChapters.find(mapped => mapped.num === chapter.num);

          if (mappedChapter?.tocId && isTocIdValidForChapter(mappedChapter.tocId, tocAsset)) {
            return {
              ...chapter,
              chapterId: mappedChapter.tocId,
              id: mappedChapter.tocId,
              title: mappedChapter.tocTitle || mappedChapter.title,
              chapterTitle: chaptersList.find(
                currentChapter => currentChapter?.id === mappedChapter.tocId
              )?.title
            };
          }

          return chapter;
        };

        let mappedChapterEntries = (coverage.chapters || []).map(mapChapterEntry);

        const provisionalSectionTocIds = coverageSectionEntries
          .map(({ chapterNum, sectionNum }) => findMappedSectionTocId(
            sectionNum, chapterNum, mappedChapters, mappedSections, tocAsset
          ))
          .filter(Boolean);

        mappedChapterEntries = mappedChapterEntries.map((chapter) => {
          if (chapter.chapterId || chapter.id || !provisionalSectionTocIds.length || !tocAsset) {
            return chapter;
          }
          const parentChapterId = findParentChapterForPage(provisionalSectionTocIds[0], tocAsset);
          if (!parentChapterId) return chapter;

          return {
            ...chapter,
            chapterId: parentChapterId,
            id: parentChapterId,
            chapterTitle: chaptersList.find(
              currentChapter => currentChapter?.id === parentChapterId
            )?.title || chapter.chapterTitle
          };
        });

        const sectionIds = [];
        const validatedSections = [];
        const seenSectionIds = new Set();
        // Chapter numbers that had at least one section resolve to a real tocId.
        const validatedChapterNums = new Set();

        coverageSectionEntries.forEach(({ chapterNum, sectionNum }) => {
          const tocId = findMappedSectionTocId(
            sectionNum, chapterNum, mappedChapters, mappedSections, tocAsset
          );
          if (!tocId || seenSectionIds.has(tocId) || !findNodeTitleById(tocId, tocAsset)) {
            return;
          }
          seenSectionIds.add(tocId);
          sectionIds.push(tocId);
          validatedSections.push(sectionNum);
          if (chapterNum != null) {
            validatedChapterNums.add(String(chapterNum));
          }
        });
        // A chapter's "sections" can be stale labels (e.g. "4.1.A") that never mapped
        // to a real tocId -they wrongly make a whole-chapter row look
        // partial later on, and it gets dropped. PPCOREAH-1614 fix
        mappedChapterEntries = mappedChapterEntries.map((chapter) => {
          // nothing to check, return as is
          if (!Array.isArray(chapter?.sections) || chapter?.sections?.length === 0) {
            return chapter;
          }
          // No num to look up, or confirmed genuinely validated — return as is
          if (chapter?.num == null || validatedChapterNums.has(String(chapter?.num))) {
            return chapter;
          }
          // actual fix - None of this chapter's sections validated against tocmapping — drop the stale labels.
          const { sections, ...rest } = chapter;

          return rest;
        });

        return {
          ...scheduleItem,
          textbookCoverage: {
            ...coverage,
            sections: validatedSections.length > 0 ? validatedSections : null,
            sectionIds: sectionIds.length > 0 ? sectionIds : [],
            pages: coverageSectionEntries.length > 0 ? [] : (coverage.pages || []),
            chapters: mappedChapterEntries
          }
        };
      }

      return scheduleItem;
    });

    const schedule = updatedData.syllabusInfo.data.schedule;
    if (updatedData.syllabusInfo.assignments?.length) {
      updatedData.syllabusInfo.assignments = updatedData.syllabusInfo.assignments.map((assignment, index) => {
        const scheduleItem = assignment.referenceId
          ? schedule.find(item => item.referenceId === assignment.referenceId)
          : schedule[index];
        if (!scheduleItem?.textbookCoverage) return assignment;

        return {
          ...assignment,
          textbookCoverage: {
            ...assignment.textbookCoverage,
            sections: scheduleItem.textbookCoverage.sections,
            sectionIds: scheduleItem.textbookCoverage.sectionIds,
            pages: scheduleItem.textbookCoverage.pages,
            chapters: scheduleItem.textbookCoverage.chapters
          }
        };
      });
    }
  }

  return updatedData;
}

export const { applyCoverageDedupeToSyllabusData } = coverageDuplicationUtils;

// common function to get title by id from toc in recursive manner
export function findNodeTitleById(nodeId, tocData) {
  if (!nodeId || !tocData) {
    return null;
  }

  // First try to find in slates if available
  if (tocData.slates && Array.isArray(tocData.slates)) {
    const foundSlate = tocData.slates.find(slate => slate.id === nodeId);
    if (foundSlate && foundSlate.title) {
      return foundSlate.title;
    }
  }

  // If not found in slates, search recursively in children tree
  const findItemById = (items) => {
    if (!items || !Array.isArray(items)) return null;
    const found = items.find(item => item.id === nodeId);
    if (found) return found;
    return items
      .map(item => (item.children ? findItemById(item.children) : null))
      .find(result => result !== null) || null;
  };

  if (tocData.children && Array.isArray(tocData.children)) {
    const foundItem = findItemById(tocData.children);
    return foundItem && foundItem.title ? foundItem.title : null;
  }

  return null;
}

export function getSectionTitleandNumber(pageId, tocData) {
  if (!pageId || !tocData) {
    return { sectionTitle: '', sectionNumber: '' };
  }
  const fullTitle = findNodeTitleById(pageId, tocData);

  if (!fullTitle) {
    return { sectionTitle: '', sectionNumber: '' };
  }

  const [sectionNumber, sectionTitle] = fullTitle.split(':');
  return {
    sectionTitle: sectionTitle?.trim() || '',
    sectionNumber: sectionNumber?.trim() || ''
  };
}

export function getPageIdsForChapter(chapterId, chapterToPageIdMap) {
  let pageIds = chapterToPageIdMap.get(chapterId) || [];

  // If no pageIds found in mapping, it might be a direct chapterId match
  if (pageIds.length === 0) {
    const existsAsPageId = Array.from(chapterToPageIdMap.values())
      .some(pageIdArray => pageIdArray.includes(chapterId));

    if (existsAsPageId) {
      pageIds = [chapterId];
    }
  }

  return pageIds;
}

// helper separated for actual push logic
function pushToContentCovered(
  contentCovered,
  pageId,
  mcqQuestions,
  tocData,
  chapterId,
  chapterTitle,
  chapterNumber,
  requireQuestions = false
) {
  const questionsForPage = mcqQuestions.filter(mcq => mcq.pageId === pageId);
  if (requireQuestions && !questionsForPage.length) return;
  const sectionMeta = getSectionTitleandNumber(pageId, tocData);
  contentCovered.push({
    chapterId,
    chapterTitle,
    chapterNumber,
    pageId,
    sectionNumber: sectionMeta?.sectionNumber,
    sectionTitle: sectionMeta?.sectionTitle,
    questions: questionsForPage.map(q => ({ questionId: q.questionId, point: q.point || 1 }))
  });
}

function getContentCovered(item, tocData) {
  const contentCovered = [];

  // Get MCQ data from the MCQ store
  const mcqStore = Framework.getStoreRegistry().getStore('mcqAssignments');
  const mcqQuestions = mcqStore?.mcqQuestions || [];
  const chapterToPageIdMap = buildChapterToPageMapping(tocData);

  if (item?.textbookCoverage?.chapters) {
    // Filter duplicate chapters based on chapterId, id, or title (whichever is present)
    const uniqueChapters = item.textbookCoverage.chapters.filter((chapter, index, self) => {
      if (chapter.chapterId) {
        return self.findIndex(ch => ch.chapterId === chapter.chapterId) === index;
      }
      if (chapter.id) {
        return self.findIndex(ch => ch.id === chapter.id) === index;
      }
      if (chapter.title) {
        return self.findIndex(ch => ch.title === chapter.title) === index;
      }
      return true;
    });

    uniqueChapters.forEach((chapter) => {
      const chapterId = chapter.chapterId || chapter.id;
      const rawSectionIds = item?.textbookCoverage?.sectionIds || [];
      const sectionIds = rawSectionIds.filter(
        (sectionId) => sectionId && findNodeTitleById(sectionId, tocData)
      );
      const selectedPageIds = (item?.textbookCoverage?.pages || [])
        .map((pageId) => (typeof pageId === 'object' ? pageId?.id : pageId))
        .filter(Boolean);
      const chapterSections = sectionIds.filter(
        (sectionId) => findParentChapterForPage(sectionId, tocData) === chapterId
      );
      const rootSections = item?.textbookCoverage?.sections || [];
      const chapterNumStr = chapter.num != null ? String(chapter.num) : null;
      const hasRootPartialSections = chapterNumStr && rootSections.some((section) => {
        const num = typeof section === 'object' ? section?.num : section;
        return num != null && String(num).startsWith(`${chapterNumStr}.`);
      });
      const isPartialChapter = (chapter.sections || []).length > 0
        || (chapter.selectedNodeId && chapterId && chapter.selectedNodeId !== chapterId)
        || chapterSections.length > 0
        || hasRootPartialSections;
      const chapterPages = chapterId && isTocIdValidForChapter(chapterId, tocData, chapterId)
        ? getPageIdsForChapter(chapterId, chapterToPageIdMap) || []
        : [];
      const chapterSelectedPages = selectedPageIds.filter((id) => chapterPages.includes(id));
      let pageIds = [];
      if (chapterSections.length > 0) {
        const leafIds = coverageDuplicationUtils.collectLeafPageIdsFromSectionIds(
          chapterSections,
          tocData
        );
        pageIds = leafIds.length > 0 ? leafIds : chapterSections;
      } else if (!isPartialChapter && chapterPages.length > 0) {
        pageIds = selectedPageIds.length > 0 ? chapterSelectedPages : chapterPages;
      } else if (isPartialChapter && chapterPages.length > 0) {
        pageIds = chapterSelectedPages;
      }

      if (pageIds.length === 0 && selectedPageIds.length > 0) {
        pageIds = selectedPageIds.filter(
          (id) => findParentChapterForPage(id, tocData) === chapterId
        );
      }

      const resolvedChapterId = (chapterId && isTocIdValidForChapter(chapterId, tocData, chapterId))
        ? chapterId
        : (pageIds[0] ? findParentChapterForPage(pageIds[0], tocData) : null);

      pageIds.forEach((pageId) => {
        pushToContentCovered(
          contentCovered,
          pageId,
          mcqQuestions,
          tocData,
          resolvedChapterId,
          chapter.chapterTitle || chapter.title,
          chapter.num
        );
      });
    });
  }

  // Later partial rows in the same chapter (e.g. 1.4-1.5 after 1.1-1.3): applyCoverageDedupeToSyllabusData clears chapters but keeps sectionIds, so the loop above never runs.
  // hence fallback by respecting the incoming sectionIds as is and recomputing

  if (!contentCovered.length && item?.textbookCoverage?.sectionIds?.length) {
    const sectionIds = item.textbookCoverage.sectionIds.filter(Boolean);
    const pageIds = coverageDuplicationUtils.collectLeafPageIdsFromSectionIds(sectionIds, tocData);
    (pageIds.length ? pageIds : sectionIds).forEach((pageId) => {
      const chapterId = findParentChapterForPage(pageId, tocData);
      const parentChapter = getFlatChaptersList(tocData).find(ch => ch.id === chapterId);
      const {
        sectionNumber: chapterNumberFromTitle,
        sectionTitle: chapterTitleFromTitle
      } = getSectionTitleandNumber(chapterId, tocData);
      const chapterTitle = chapterTitleFromTitle
        || parentChapter?.title
        || findNodeTitleById(chapterId, tocData);
      const chapterNumber = parentChapter?.num ?? chapterNumberFromTitle ?? null;

      pushToContentCovered(
        contentCovered,
        pageId,
        mcqQuestions,
        tocData,
        chapterId,
        chapterTitle,
        chapterNumber,
        true
      );
    });
  }

  return contentCovered;
}

export function createISODateWithTime(dateString, timeString = TIME_FORMATS.DEFAULT) {
  if (!dateString) return null;
  const date = CommonUtils.parseDateString(dateString);
  const clean = (timeString || '').trim();
  const [h, m] = clean.includes(':')
    ? clean.split(':').map(Number)
    : [NaN, NaN];
  // Apply defaults when invalid
  const hours = Number.isFinite(h) ? h : TIME_FORMATS.DEFAULT_HOUR;
  const minutes = Number.isFinite(m) ? m : TIME_FORMATS.DEFAULT_MINUTE;
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
}

/** Final deduplication helper before create call */
function dedupeAssignmentCoverageAcrossRows(assignments = []) {
  const takenPageIds = new Set();
  const takenChapterIds = new Set();

  return assignments.reduce((result, assignment) => {
    const contentCovered = (assignment.contentCovered || []).filter((entry) => {
      const pageId = entry?.pageId;
      const chapterId = entry?.chapterId;

      if (pageId) {
        if (takenPageIds.has(pageId)) return false;
        takenPageIds.add(pageId);

        return true;
      }

      if (chapterId) {
        if (takenChapterIds.has(chapterId)) return false;
        takenChapterIds.add(chapterId);

        return true;
      }

      return false;
    });

    if (contentCovered.length === 0) return result;

    result.push({ ...assignment, contentCovered });

    return result;
  }, []);
}

// Helper function to transform syllabus data to assignment format
export function transformSyllabusToAssignments(syllabusData, bookId, courseId, tocData) {
  if (!syllabusData?.syllabusInfo?.assignments) {
    return [];
  }

  const bookshelf = Framework.getStoreRegistry().getStore('bookshelf');
  const availableBeforeDueDays = bookshelf?.getAvailableBeforeDueDays?.(courseId) ?? 0;
  const defaultDueTime = bookshelf?.getAssignmentsSettingsConfig?.(
    courseId, ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS.DEFAULT_DUE_TIME, TIME_FORMATS.DEFAULT
  );
  const courseTimeZone = bookshelf?.getAssignmentsSettingsConfig?.(
    courseId, ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS.COURSE_TIME_ZONE
  );

  return dedupeAssignmentCoverageAcrossRows(
    syllabusData.syllabusInfo.assignments.map((item, index) => {
      const schedule = syllabusData.syllabusInfo?.data?.schedule;
      const scheduleItem = (item.referenceId && schedule
        ? schedule.find(row => row.referenceId === item.referenceId)
        : null) ?? schedule?.[index];
      const dueDate = item.dueDate || item.date;
      const time = item.time || defaultDueTime;
      const timeZone = item.timeZone || scheduleItem?.timeZone || courseTimeZone;

      // Use timezone-aware conversion when a timezone is present, otherwise fall back to local
      let formattedDueDate = null;
      if (dueDate && timeZone) {
        formattedDueDate = dateTimeUtils.wallClockToUTC(dueDate, time, timeZone)?.toISOString()
          ?? createISODateWithTime(dueDate, time);
      } else if (dueDate) {
        formattedDueDate = createISODateWithTime(dueDate, time);
      }

      const dateAvailable = computeDateAvailable(formattedDueDate, availableBeforeDueDays);

      return ({
        title: item.title,
        referenceId: item.referenceId,
        assignmentStatus: ASSIGNMENT_STATUS_TYPES.ASSIGNED,
        dateAvailable,
        dueDate: formattedDueDate,
        timeZone,
        bookId,
        assignmentType: ASSIGNMENT_TYPES.QUIZ,
        createdBy: CommonUtils.getCurrentUserId(),
        contentCovered: getContentCovered(
          { ...item, textbookCoverage: scheduleItem?.textbookCoverage ?? item.textbookCoverage },
          tocData
        ),
        dateNormalizationAction: scheduleItem?.dateNormalizationAction
          ?? item.dateNormalizationAction
          ?? null,
        coverageDedupeAction: scheduleItem?.coverageDedupeAction
          ?? item.coverageDedupeAction
          ?? null,
        validationErrors: scheduleItem?.validationErrors ?? item.validationErrors ?? []
      });
    })
  );
}

/**
 * Map assessmentId to each question
 * @param {Array} assignments - Array of assignment objects
 * @returns {Array} - Array of assignment objects with assessmentId mapped to each question
 */
export function mapRootAssessmentToEachQuestion(assignments) {
  if (!assignments || !Array.isArray(assignments)) {
    return [];
  }

  return assignments.map((assignment) => {
    const { assessmentId, contentCovered } = assignment || {};

    // No assessmentId or no content covered
    if (!assessmentId || !contentCovered || contentCovered.length === 0) {
      return assignment;
    }

    // Map assessmentId to each question since same assessmentId is used for all questions in the assignment
    return {
      ...assignment,
      assessmentId,
      contentCovered: contentCovered.map(content => ({
        ...content,
        questions: (content.questions && content.questions.length > 0) ? content.questions.map(question => ({
          ...question,
          assessmentId
        })) : []
      }))
    };
  });
}


export function areAllPagesSelected(chapterId, selectedPageIds, chapterToPageIdMap) {
  const allPageIdsForChapter = getPageIdsForChapter(chapterId, chapterToPageIdMap) || [];

  if (allPageIdsForChapter.length === 0) {
    return false;
  }

  return allPageIdsForChapter.length === selectedPageIds.length
    && allPageIdsForChapter.every(pageId => selectedPageIds.includes(pageId));
}

function buildAssignmentTags(contentCovered, tocData) {
  if (!contentCovered || contentCovered.length === 0) {
    return [];
  }
  // If no TOC data, fallback to chapter titles
  if (!tocData || (!tocData.children && !tocData.slates)) {
    return contentCovered
      .map(content => content.chapterTitle)
      .filter(title => title && title.trim() !== '')
      .filter((title, index, array) => array.indexOf(title) === index);
  }

  const chapterToPageIdMap = buildChapterToPageMapping(tocData);
  const assignmentTags = [];

  // Group contentCovered by chapterId and collect selectedPageIds
  const chapterMap = new Map();
  contentCovered.forEach((content) => {
    if (!content.chapterId) return;

    if (!chapterMap.has(content.chapterId)) {
      chapterMap.set(content.chapterId, {
        chapterId: content.chapterId,
        chapterTitle: content.chapterTitle,
        selectedPageIds: []
      });
    }

    if (content.pageId) {
      chapterMap.get(content.chapterId).selectedPageIds.push(content.pageId);
    }
  });

  chapterMap.forEach((chapterData) => {
    const { chapterId, chapterTitle, selectedPageIds } = chapterData;

    if (areAllPagesSelected(chapterId, selectedPageIds, chapterToPageIdMap) && chapterTitle) {
      // All pages selected - use chapter title
      assignmentTags.push(chapterTitle);
    } else {
      // Partial selection - get page titles from TOC by matching pageId
      contentCovered
        .filter(content => content.chapterId === chapterId && content.pageId)
        .forEach((content) => {
          const pageTitle = findNodeTitleById(content.pageId, tocData);
          if (pageTitle && !assignmentTags.includes(pageTitle)) {
            assignmentTags.push(pageTitle);
          }
        });
    }
  });

  return assignmentTags;
}

/**
 * Few fields from old response are no longer present in new
 * Inside codebase we have used id and assignmentTags
 * Inside of modifying all places, we set a mapper before pushing to store
 * @param {*} assignment
 * @returns
 */
export function transformAssignmentResponse(assignment) {
  let tocData = null;
  try {
    const assetStore = Framework.getStoreRegistry().getStore('asset');
    tocData = assetStore ? getSnapshot(assetStore) : null;
  } catch (e) {
    console.warn('Could not access asset store for assignment tags:', e);
  }

  const assignmentTags = buildAssignmentTags(assignment.contentCovered, tocData);

  return {
    ...assignment,
    assignmentId: assignment?.assignmentId || assignment?.id || null,
    assignmentTags
  };
}

function extractTimeFromISO(isoDate) {
  if (!isoDate) return TIME_FORMATS.DEFAULT;
  try {
    const date = new Date(isoDate);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch (e) {
    return TIME_FORMATS.DEFAULT;
  }
}

/**
 * Whether the course has copied, unpublished assignments (ASSIGNABLE status).
 * With assignmentStatus=ALL, checks any row — not only the first assignment.
 * @param {Array} assignments
 * @returns {boolean}
 */
export function isAssignmentCopiedInCourse(assignments = []) {
  return assignments.some(
    (assignment) => assignment.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNABLE
  );
}

/**
* transforms assignments back to syllabus format
* @param {Array} assignments - Array of assignment objects
* @param {?string} courseTimeZone - Course-level default timeZone (bookshelf.getAssignmentsSettingsConfig),
*   used only when an assignment carries no timeZone of its own
*/
export function transformAssignmentsToSyllabus(assignments, courseTimeZone = null) {
  if (!assignments || !Array.isArray(assignments)) {
    return {
      syllabusInfo: {
        assignments: []
      }
    };
  }
  const syllabusAssignments = assignments.map((assignment) => {
    const textbookCoverage = {
      chapters: []
    };
    if (assignment.contentCovered && Array.isArray(assignment.contentCovered)) {
      const chapterMap = new Map();
      assignment.contentCovered.forEach((content) => {
        // Create unique key for chapter+page combination
        const uniqueKey = `${content.chapterId}_${content.pageId}`;
        chapterMap.set(uniqueKey, {
          id: content.chapterId,
          title: content.chapterTitle,
          num: content.chapterNumber,
          chapterTitle: content.chapterTitle,
          selectedNodeId: content.pageId
        });
      });

      textbookCoverage.chapters = Array.from(chapterMap.values());
      textbookCoverage.pages = assignment.contentCovered.map(content => content.pageId);
    }

    const dueDate = assignment.dueDate;
    const hasDueDate = Boolean(dueDate);

    const resolvedTimeZone = assignment.timeZone || courseTimeZone || null;
    const wallClockInOwnTz = hasDueDate
      ? dateTimeUtils.utcToWallClock(dueDate, resolvedTimeZone)
      : null;

    return {
      id: assignment.id,
      title: assignment.title,
      date: wallClockInOwnTz?.date || (hasDueDate ? CommonUtils.formatDate(dueDate, YEAR_MONTH_DAY_FORMAT) : ''),
      dueDate: dueDate || '',
      timeZone: wallClockInOwnTz ? resolvedTimeZone : (assignment.timeZone || null),
      textbookCoverage: textbookCoverage.chapters.length > 0 ? textbookCoverage : undefined,
      type: assignment.assignmentType === ASSIGNMENT_TYPES.QUIZ ? 'quiz' : assignment.assignmentType,
      time: wallClockInOwnTz?.time || (hasDueDate ? extractTimeFromISO(dueDate) : '')
    };
  });

  return syllabusAssignments;
}

/**
* make Bulk Update Payload
* @param {Array} assignments - Array of assignment objects
*/
export function createBulkUpdatePayload(assignments) {
  if (!assignments || !Array.isArray(assignments)) {
    return null;
  }

  const payload = assignments.map((assignemnt) => {
    const timeZone = assignemnt.timeZone || null;
    // if timezone present, use timezone-aware conversion
    const computedDueDate = timeZone
      ? (dateTimeUtils.wallClockToUTC(assignemnt.date, assignemnt.startTime, timeZone)?.toISOString()
          ?? createISODateWithTime(assignemnt.date, assignemnt.startTime))
      : createISODateWithTime(assignemnt.date, assignemnt.startTime);

    return {
      id: assignemnt.id,
      dueDate: computedDueDate
    };
  });

  return payload;
}


/**
 * Map itemId to each question in the content covered
 * @param {Array} contentCovered - Array of content covered objects
 * @param {Array} existingContentCovered - Array of existing content covered objects
 * @returns {Array} - Array of content covered objects with itemId mapped to each question
 */
export function mapItemIdToQuestion(contentCovered, existingContentCovered) {
  if (!contentCovered || !Array.isArray(contentCovered)) {
    return [];
  }

  if (!existingContentCovered || !Array.isArray(existingContentCovered)) {
    return contentCovered;
  }

  return contentCovered.map((content) => {
    const existingContent = existingContentCovered.find(
      c => c.chapterId === content.chapterId && c.pageId === content.pageId
    );

    if (!existingContent) {
      return content;
    }

    return {
      ...content,
      questions: content.questions.map((question) => {
        const itemId = existingContent.questions?.find(q => q.questionId === question.questionId)?.itemId;

        if (!itemId) {
          return question;
        }

        return {
          ...question,
          itemId
        };
      }) || []
    };
  });
}

/**
 * Method to get syllabus state dependencies
 */
export const getSyllabusStateDependencies = () => {
  const syllabusStore = Framework.getStoreRegistry().getStore('syllabus');
  const syllabusSnapshot = getSnapshot(syllabusStore);

  return {
    scheduleLength: syllabusSnapshot?.syllabusInfo?.data?.schedule?.length || 0,
    titlesGenerated: syllabusSnapshot?.syllabusInfo?.data?.schedule
      ?.filter(s => s.isTitleGenerated).length || 0,
    chapterMappingLength: syllabusSnapshot?.chapterMCQMapping?.length || 0,
    assignmentsLength: syllabusSnapshot?.syllabusInfo?.assignments?.length || 0,
    chaptersInSchedule: syllabusSnapshot?.syllabusInfo?.data?.schedule
      ?.reduce((count, item) => count + (item.textbookCoverage?.chapters?.length || 0), 0) || 0
  };
};

/**
 * True when every ASSIGNABLE row has a due date (copied-course publish gating).
 * @param {Array} assignments
 * @returns {boolean}
 */
export function allAssignableRowsHaveDueDates(assignments = []) {
  const assignable = assignments.filter(
    (row) => row.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNABLE
  );

  if (assignable.length === 0) {
    return false;
  }

  const allHaveDueDates = assignable.every(
    (row) => Boolean(row.dueDate || row.dateDue)
  );

  if (!allHaveDueDates) {
    return false;
  }
  // check if any assignable row is publish eligible

  return assignable.some(isAssignableRowPublishEligible);
}

/**
 * helper function to check if an assignable row is publish eligible
 */
export function isAssignableRowPublishEligible(row) {
  const dueDate = row?.dueDate || row?.dateDue;

  return Boolean(dueDate) && !hasAssignmentDueDatePassed(dueDate);
}

/**
 * Draft save requires title and chapter mapping on every row.
 * @param {Array} schedule
 * @param {Array} tocData
 * @returns {boolean}
 */
export function canSaveScheduleAsDraft(schedule = [], tocData = []) {
  if (!schedule.length) {
    return false;
  }

  return schedule.every((row) => {
    const title = String(row.title || row.assignmentTitle || '').trim();

    if (!title) {
      return false;
    }

    if (validationUtils.validateMappedChapters(row, tocData)) {
      return false;
    }

    return true;
  });
}

/**
 * Dashboard manual create payloads should remain unpublished until publish.
 * @param {Array} assignments
 * @returns {Array}
 */
export function applyDashboardAssignableStatus(assignments = []) {
  return assignments.map((row) => ({
    ...row,
    assignmentStatus: ASSIGNMENT_STATUS_TYPES.ASSIGNABLE,
    isAssigned: false
  }));
}

/**
 * API payloads to publish ASSIGNABLE rows (status → ASSIGNED).
 * @param {Array} storeAssignments
 * @param {{ assignmentId?: string }} [options]
 * @returns {Array}
 */
export function buildCopiedPublishApiPayloads(storeAssignments = [], { assignmentId } = {}) {
  let assignableRows = storeAssignments.filter(
    (row) => row.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNABLE
  );

  if (assignmentId) {
    assignableRows = assignableRows.filter(
      (row) => (row.assignmentId || row.id) === assignmentId
    );
  }
  // filter out assignable rows that are not publish eligible

  return assignableRows.filter(isAssignableRowPublishEligible).map((row) => {
    const dueDate = row.dueDate || row.dateDue;

    return {
      id: row.assignmentId || row.id,
      dueDate,
      assignmentStatus: ASSIGNMENT_STATUS_TYPES.ASSIGNED,
      isAssigned: true,
      dateAvailable: row.dateAvailable,
      availableAfterDueDate: true
    };
  });
}

/** @deprecated Use buildCopiedPublishApiPayloads */
export function buildCopiedBulkPublishApiPayloads(storeAssignments = []) {
  return buildCopiedPublishApiPayloads(storeAssignments);
}

/**
 * Method to check Assignments Due ThisWeek or not
 */
export const isAssignmentsDueThisWeek = () => {
  const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
  const dueThisWeekAssignments = assignmentStore.getDueThisWeekAssignmentList();

  return dueThisWeekAssignments?.length > 0;
};

/**
 * save last added date, time and courseId to sessionstorage
 */
export const saveLastAssignmentDefaults = (data, courseId) => {
  try {
    const payload = { ...data, courseId };
    CommonUtils.setSessionStorge(LAST_ASSIGNMENT_DEFAULTS_KEY, payload, true);
  } catch (e) {
    console.warn('Failed to save assignment defaults to sessionStorage', e);
  }
};

/**
 * clears out above key
 */
export const clearLastAssignmentDefaults = () => {
  try {
    CommonUtils.clearSessionStorge(LAST_ASSIGNMENT_DEFAULTS_KEY);
  } catch (err) {
    console.warn('Failed to clear last assignment defaults', err);
  }
};

export const getLastAssignmentDefaults = (currentCourseId) => {
  try {
    const data = CommonUtils.getSessionStorge(LAST_ASSIGNMENT_DEFAULTS_KEY, true);
    if (!data) return null;

    // Different course - wipe and return null
    if (data.courseId !== currentCourseId) {
      clearLastAssignmentDefaults();
      return null;
    }
    // Validate date/time not in past
    const iso = createISODateWithTime(data?.date, data?.time);
    if (!iso) {
      clearLastAssignmentDefaults();
      return null;
    }
    const fullDate = new Date(iso);
    if (Number.isNaN(fullDate.getTime()) || fullDate.getTime() < Date.now()) {
      clearLastAssignmentDefaults();
      return null;
    }
    return data;
  } catch (e) {
    console.warn('Failed to get assignment defaults from sessionStorage', e);
    return null;
  }
};

/** Default due date string (yyyy-MM-dd), never before today. */
const toDefaultDueDateOnOrAfterToday = (computedDateStr) => {
  const todayStr = new Date().toISOString().slice(0, 10);
  const dateStr = computedDateStr < todayStr ? todayStr : computedDateStr;

  return dateStr;
};

/**
 * Default due date/time for a new assignment form.
 * First assignment in course: course start + 7 days @ 23:59.
 * Subsequent: previous assignment date/time + 7 days, with fallback to first-assignment rule.
 */
export const getCreateAssignmentDefaults = (courseId, isFirstAssignment = false) => {
  const bookshelf = Framework.getStoreRegistry().getStore('bookshelf');
  const defaultDueTime = bookshelf?.getAssignmentsSettingsConfig?.(
    courseId, ASSIGNMENT_SETTINGS_CONFIGURATION_KEYS.DEFAULT_DUE_TIME, TIME_FORMATS.DEFAULT
  );

  if (!isFirstAssignment) {
    const lastDefaults = getLastAssignmentDefaults(courseId);

    if (lastDefaults?.date) {
      const time = lastDefaults.time || defaultDueTime;
      const iso = createISODateWithTime(lastDefaults.date, time);
      const dueDate = iso ? new Date(iso) : null;

      if (dueDate && !Number.isNaN(dueDate.getTime())) {
        dueDate.setDate(dueDate.getDate() + 7);

        return {
          date: toDefaultDueDateOnOrAfterToday(
            CommonUtils.formatDate(dueDate, YEAR_MONTH_DAY_FORMAT)
          ),
          time: extractTimeFromISO(dueDate.toISOString())
        };
      }
    }
  }

  const courseStart = CommonUtils.getCourseStartDateFromHeroBook();

  if (!courseStart) {
    return null;
  }

  const dueDate = new Date(courseStart);

  if (Number.isNaN(dueDate.getTime())) {
    return null;
  }

  dueDate.setDate(dueDate.getDate() + 7);

  return {
    date: toDefaultDueDateOnOrAfterToday(dueDate.toISOString().slice(0, 10)),
    time: defaultDueTime
  };
};

/**
 * Checks if a title needs sanitization based on length and special characters
 */
const needsTitleSanitization = (title) => {
  if (!title || typeof title !== 'string') {
    return false;
  }
  const maxLength = getContainerContextValue(ASSIGNMENT_TITLE_CONFIG.MAX_LENGTH_KEY, DEFAULT_MAX_ASSIGNMENT_TITLE_LENGTH);
  const pattern = getContainerContextValue(ASSIGNMENT_TITLE_CONFIG.SUPPORTED_CHAR_KEY, DEFAULT_SUPPORTED_CHAR);
  const exceedsMaxLength = new RegExp(`^[\\w\\s]{1,${maxLength}}$`);
  const hasSpecialChars = new RegExp(pattern);
  return !exceedsMaxLength.test(title) || hasSpecialChars.test(title);
};

/**
 * Determines the isTitleGenerated flag for an assignment item
 */
export const getTitleGeneratedFlag = (item) => {
  // if already title present, yes in case of upload syllabus
  // we do sanity check and then conditionally reprocess it by berlin
  if (item.title) {
    return !needsTitleSanitization(item.title);
  }
  return item.isTitleGenerated || false;
};

/**
 * Returns index as a sortOrder for a given id
 */
export const getTocSortOrder = (flatToc, id) => {
  const index = flatToc
    .findIndex(tocItem => CommonUtils.localStringCompare(String(tocItem.id), String(id)));
    // not found, push to last of the list
  return index !== -1 ? index : Infinity;
};

/**
 * Sorts contentCovered by index from toc
 * @param {Array} contentCovered - Array of content covered objects
 * @returns {Array} - Sorted content covered objects
 */
export const sortContentByPlayOrder = (contentCovered) => {
  if (!contentCovered || contentCovered.length <= 1) return contentCovered;
  const asset = Framework.getStoreRegistry().getStore('asset');

  if (!asset) return contentCovered;
  const assetData = getSnapshot(asset);
  const flatToc = PlaylistUtils.flattenToc(assetData?.children || []);
  // if no flatToc, return contentCovered as is
  if (flatToc.length === 0) return contentCovered;

  // get index, then use it to sort contentCovered
  return [...contentCovered].sort(
    (a, b) => getTocSortOrder(flatToc, a?.pageId || a?.chapterId)
            - getTocSortOrder(flatToc, b?.pageId || b?.chapterId)
  );
};

/**
 * Sorts assignments by chapter
 * @param {Array} assignments - Array of assignment objects
 * @returns {Array} - Sorted assignments objects
 */
export const getSortedAssignmentsByChapterOrSectionOrder = (assignments) => {
  const asset = Framework.getStoreRegistry().getStore('asset');
  if (!asset) return assignments;
  const assetData = getSnapshot(asset);
  const flatToc = PlaylistUtils.flattenToc(assetData?.children || []);
  // if no flatToc, return assignments as is
  if (flatToc.length === 0) return assignments;

  const sortedData = [...assignments].sort((a, b) => {
    // Only sort if dueDate is same
    if (a.dueDate !== b.dueDate) return 0;

    const largerIndex = getTocSortOrder(flatToc, a?.contentCovered[0]?.pageId || a?.contentCovered[0]?.chapterId);
    const smallerIndex = getTocSortOrder(flatToc, b?.contentCovered[0]?.pageId || b?.contentCovered[0]?.chapterId);

    return largerIndex - smallerIndex;
  });

  return sortedData;
};

/**
 * get single ExtraMaterial Used Type
 * @param {Array} extraMaterialUsedType - Array of extraMaterialUsed
 * @returns {Boolean}
 */
export const getExtraMaterialUsedType = (extraMaterialUsedType, type) => {
  if (!Array.isArray(extraMaterialUsedType || !type)) {
    return false;
  }
  const item = extraMaterialUsedType.find(MaterialUsedType => MaterialUsedType.type === type);

  return item ? item?.frequency > 0 : false;
};
